import { BillingDto } from '@api/client';
import { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'core/store/hooks';
import { getBillingPlan } from '../_redux/actions';
import { billingDataSelector, isBillEditEnabledSelector } from '../_redux/selectors';
import { EditableStatoAvanzamentoLavori, generateTempSalId } from './useEditBill';

const useGetBillingData = (projectId: number) => {
  const dispatch = useAppDispatch();
  const billingSelector = useAppSelector(billingDataSelector);
  const isEditEnabled = useAppSelector(isBillEditEnabledSelector);
  const [billingPlan, setBillingPlan] = useState<BillingDto[] | undefined>(undefined);

  useEffect(() => {
    if (!billingSelector) {
      dispatch(getBillingPlan(projectId));
    }
  }, []);

  useEffect(() => {
    if (billingSelector) {
      setBillingPlan((prevBillingPlan) =>
        billingSelector.map((item) => {
          // Le righe SAL bozza (tempId, mai salvate sul BE) vivono solo nello stato locale:
          // un refetch (es. dopo il salvataggio di UN'ALTRA riga, che essendo globale fa
          // rigirare questo effect anche per fatture non coinvolte nel salvataggio)
          // sovrascriverebbe l'intero billingPlan e le cancellerebbe silenziosamente se non
          // le riportiamo qui. Il placeholder (`isEmptyPlaceholder`) è gestito a parte
          // (sotto): la sua sopravvivenza dipende solo dall'esistenza o meno di un SAL reale
          // sul BE per questa milestone.
          const prevItem = prevBillingPlan?.find((prev) => prev.id === item.id);
          const prevSalList = prevItem?.statoAvanzamentoLavoriList as EditableStatoAvanzamentoLavori[] | undefined;
          const genuineDraftRows =
            prevSalList?.filter((sal) => sal.id === undefined && !sal.isEmptyPlaceholder) ?? [];

          const beSalList = item.statoAvanzamentoLavoriList ?? [];

          let mergedList: EditableStatoAvanzamentoLavori[];
          if (beSalList.length === 0) {
            // Prima di questa feature ogni fattura mostrava sempre 1 riga (anche con SAL
            // nullo). Finché il BE non ha ALCUN SAL salvato per questa milestone, il
            // placeholder nativo resta il "padre": l'aggiunta di figli è disabilitata finché
            // il padre non è salvato, quindi qui non può mai coesistere una bozza figlia
            // genuina. Riusiamo il placeholder già presente in stato locale (per non perdere
            // modifiche non salvate fatte direttamente su di esso, es. importo/data
            // compilati) oppure ne creiamo uno nuovo se non esisteva ancora.
            // `isEmptyPlaceholder` lo marca per nascondere il bottone elimina su di lui.
            const existingPlaceholder = prevSalList?.find((sal) => sal.isEmptyPlaceholder);
            const placeholder: EditableStatoAvanzamentoLavori = existingPlaceholder ?? {
              tempId: generateTempSalId(),
              isEmptyPlaceholder: true,
              isInviato: false,
              idTipologiaBlocco: { value: 'LAVORAZIONI_IN_CORSO' },
            };
            mergedList = [placeholder];
          } else {
            // Appena esiste almeno un SAL reale sul BE per questa milestone, il placeholder
            // sintetico ha esaurito il suo scopo e non va più riportato: sopravvivono solo i
            // record reali e le bozze figlie genuine ancora da salvare.
            mergedList = [...beSalList, ...genuineDraftRows];
          }

          return {
            ...item,
            // Il BE ordina già i record per data di creazione, ma ordiniamo esplicitamente
            // per garantire che il primo elemento dell'array sia sempre il record SAL "padre"
            // (il più vecchio) della milestone.
            statoAvanzamentoLavoriList: mergedList
              // Le bozze non hanno creationDate: il fallback a MAX_SAFE_INTEGER le tiene sempre in coda
              // (Infinity - Infinity darebbe NaN, comparator non ben definito), invece di farle
              // finire in testa (e passare per "padre") come con un fallback a??0/b??0 pari a 0.
              .sort(
                (a, b) =>
                  (a.creationDate?.getTime() ?? Number.MAX_SAFE_INTEGER) -
                  (b.creationDate?.getTime() ?? Number.MAX_SAFE_INTEGER)
              )
              .map((sal) => ({
                ...sal,
                idTipologiaBlocco: {
                  ...sal.idTipologiaBlocco,
                  value: sal.idTipologiaBlocco?.value || 'LAVORAZIONI_IN_CORSO',
                },
              })),
          };
        })
      );
    }
  }, [billingSelector]);

  return { billingPlan, setBillingPlan, isEditEnabled };
};

export default useGetBillingData;
